import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PEAK_DAY_DEMAND_PER_ERC, SET_PEAK_DAY_DEMAND_PER_ERC } from '../../../../../queries/engineeringQrys';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const INITIAL_FORM_STATE = {
  peakDayVolumeValue: '',
  peakDayVolumeError: '',
  adjustedPeakDayErcValue: '',
  adjustedPeakDayErcError: '',
  changeJustificationValue: '',
  changeJustificationError: '',
};

const DwriPeakDayDemandPerErc = ({ pwsId, years, onSaved }) => {

  const [editRow, setEditRow] = React.useState(null);
  const [formState, setFormState] = useImmer(INITIAL_FORM_STATE);
  const [mutationError, setMutationError] = React.useState('');

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };

  const canEditWaterUseSystem = user?.permissions?.includes('edit_water_use_system');

  const [setPeakDayDemand] = useMutation(SET_PEAK_DAY_DEMAND_PER_ERC);

  const { data, loading, error, refetch } = useQuery(GET_PEAK_DAY_DEMAND_PER_ERC, {
      variables: { pwsId, years },
      skip: !pwsId || !years || years.length === 0,
      fetchPolicy: 'cache-and-network',
  });

  if (error) return <p className="text-danger">Error loading Peak Day Demand per ERC</p>;
  if (loading) return <p>Loading...</p>;

  function handleEditClick(row) {
    const roundedPeakDayVolume = row.peakDayVolume == null
      ? ''
      : Number(row.peakDayVolume).toFixed(2);

    setEditRow(row);
    setFormState({
      ...INITIAL_FORM_STATE,
      peakDayVolumeValue: roundedPeakDayVolume,
      adjustedPeakDayErcValue: row.adjustedPeakDayErc == null ? '' : String(row.adjustedPeakDayErc),
      changeJustificationValue: row.changeJustification ?? '',
    });
    setMutationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setFormState(INITIAL_FORM_STATE);
    setMutationError('');
  }

  function setFormValue(field, value) {
    setFormState((draft) => {
      draft[field] = value;
      if (field === 'peakDayVolumeValue') {
        draft.peakDayVolumeError = '';
      }
      if (field === 'adjustedPeakDayErcValue') {
        draft.adjustedPeakDayErcError = '';
      }
      if (field === 'changeJustificationValue') {
        draft.changeJustificationError = '';
      }
    });
  }

  function handleNumericFieldChange(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);

    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
    });
  }

  function handleNumericFieldBlur(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
    });
  }

  function isPeakDayErcChanged() {
    if (!editRow) {
      return false;
    }
    return editRow.adjustedPeakDayErc !== (formState.adjustedPeakDayErcValue === '' ? null : Number(formState.adjustedPeakDayErcValue));
  }

  async function handleSave() {
    const nextPeakDayVolumeError = !formState.peakDayVolumeValue.trim()
      ? 'Peak Day Volume is required.'
      : (() => {
          const volumeResult = numbersUpToTwoDecimalPlaces(formState.peakDayVolumeValue);
          return volumeResult === true ? '' : volumeResult;
        })();

    const nextAdjustedPeakDayErcError = formState.adjustedPeakDayErcValue !== ''
      ? (() => {
          const ercResult = numbersUpToTwoDecimalPlaces(formState.adjustedPeakDayErcValue);
          return ercResult === true ? '' : ercResult;
        })()
      : '';

    const nextChangeJustificationError = formState.adjustedPeakDayErcValue !== '' && !formState.changeJustificationValue.trim()
      ? 'Justification is required when setting an Adjusted Peak Day ERC.'
      : '';

    setFormState((draft) => {
      draft.peakDayVolumeError = nextPeakDayVolumeError;
      draft.adjustedPeakDayErcError = nextAdjustedPeakDayErcError;
      draft.changeJustificationError = nextChangeJustificationError;
    });

    if (nextPeakDayVolumeError || nextAdjustedPeakDayErcError || nextChangeJustificationError) {
      return;
    }

    setMutationError('');
    try {
      const response = await setPeakDayDemand({
        variables: {
          updatePeakDayDemandInput: {
            pwsId,
            year: editRow.year,
            peakDayGallonsPerDay: formState.peakDayVolumeValue === '' ? null : Number(formState.peakDayVolumeValue),
            adjustedPeakDayErc: formState.adjustedPeakDayErcValue === '' ? null : Number(formState.adjustedPeakDayErcValue),
            changeJustification: formState.changeJustificationValue,
          },
        },
        errorPolicy: 'all',
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setMutationError(responseErrorMessage);
        return;
      }

      if (!response?.data?.updatePeakDayDemandPerErc) {
        setMutationError('Failed to update adjusted peak day demand per ERC.');
        return;
      }

      refetch();
      onSaved?.();
      handleModalClose();
    } catch (mutationErr) {
      setMutationError(mutationErr?.message || 'Failed to update adjusted peak day demand per ERC.');
    }
  }

  const rows = (data?.getPeakDayDemandPerErc || []).map((row) => ({
    key: `${row.deqId}-${row.year}`,
    year: row.year,
    peakDay: row.peakDate,
    peakDayVolume: row.peakDayGallonsPerDay,
    peakDayWholesaleVolume: row.peakDayWholesaleGallonsPerDay,
    peakDayErcGallons: row.peakDayErcGallons,
    adjustedPeakDayErc: row.adjustedPeakDayErc,
    changeJustification: row.changeJustification,
  }));


  return (
    <>
      <Row>
        <Col><span><b>Peak Day Demand per ERC (Gal/Day)</b></span></Col>
      </Row>
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day Volume</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day Wholesale Volume</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjusted Peak Day ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Change Justification</b></span></Col>
          {canEditWaterUseSystem && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>
        {rows.map((row, idx) => (
          <Row key={row.key} className={`dwri-service-connections-row gx-0 ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{row.peakDay}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.peakDayVolume)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.peakDayWholesaleVolume)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.peakDayErcGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.adjustedPeakDayErc)}</span></Col>
            <Col className="dwri-table-cell"><span>{row.changeJustification}</span></Col>
            {canEditWaterUseSystem && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit service connections for year ${row.year}`}
                type="button"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </button>
            </Col>
            )}
          </Row>
        ))}
      </div>
      {editRow && (
      <Modal
        onClose={handleModalClose}
        onEscape={handleModalClose}
      >
        <ModalTitle>Update Peak Day Demand per ERC (gal/day)</ModalTitle>
        <ModalContent>
          <div className="dwri-peak-day-demand-modal-grid">
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
              <TextInput label="Peak Day" value={String(editRow.peakDay ?? '')} isDisabled />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput
                label="Peak Day Volume"
                value={formState.peakDayVolumeValue}
                onChange={(event) => handleNumericFieldChange('peakDayVolumeValue', 'peakDayVolumeError', event)}
                onBlur={(event) => handleNumericFieldBlur('peakDayVolumeValue', 'peakDayVolumeError', event)}
                errorMessage={formState.peakDayVolumeError || undefined}
              />
              <TextInput label="Peak Day Wholesale Volume" value={formatNumber(editRow.peakDayWholesaleVolume ?? '')} isDisabled />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput label="Peak Day ERC" value={formatNumber(editRow.peakDayErcGallons ?? '')} isDisabled />
              <TextInput
                label="Adjusted Peak Day ERC"
                value={formState.adjustedPeakDayErcValue}
                onChange={(event) => handleNumericFieldChange('adjustedPeakDayErcValue', 'adjustedPeakDayErcError', event)}
                onBlur={(event) => handleNumericFieldBlur('adjustedPeakDayErcValue', 'adjustedPeakDayErcError', event)}
                errorMessage={formState.adjustedPeakDayErcError || undefined}
              />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
              <TextArea
                label="Peak Day Demand Change Justification"
                value={formState.changeJustificationValue}
                onChange={(event) => setFormValue('changeJustificationValue', event.target.value)}
                errorMessage={formState.changeJustificationError || undefined}
                isRequired={isPeakDayErcChanged()}
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="dwri-service-modal-footer">
          {mutationError && (
            <div className="text-danger">{mutationError}</div>
          )}
          <button type="button" onClick={handleModalClose}>Close</button>
          <button type="button" onClick={handleSave}>Save</button>
        </ModalFooter>
      </Modal>
    )}
    </>
  );
}

export default DwriPeakDayDemandPerErc;
